import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBanFromOrg } from "@/hooks/useBanFromOrg";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useUpdateChatCache } from "@zeemee/react-web-chat";

interface BanFromOrgProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  userId: string;
}

export const BanFromOrg: React.FC<BanFromOrgProps> = ({
  open,
  onOpenChange,
  orgId,
  userId,
}) => {
  const { t } = useTranslation();
  const [banFromOrg, { loading }] = useBanFromOrg();
  const { removeUsersChatMessagesFromCommunity } = useUpdateChatCache();

  const handleConfirmBan = async () => {
    try {
      const result = await banFromOrg(orgId, userId);

      if (result.success) {
        toast.success(t("banFromOrg.successMessage"), {
          action: {
            label: t("global.close"),
            onClick: () => {
              // Close action - no additional logic needed
            },
          },
        });
        onOpenChange(false);
        removeUsersChatMessagesFromCommunity(userId);
      } else {
        toast.error(t("banFromOrg.errorTitle"), {
          description: t("banFromOrg.errorMessage"),
          action: {
            label: t("global.close"),
            onClick: () => {
              // Close action - no additional logic needed
            },
          },
        });
      }
    } catch {
      toast.error(t("banFromOrg.errorTitle"), {
        description: t("banFromOrg.errorMessage"),
        action: {
          label: t("global.close"),
          onClick: () => {
            // Close action - no additional logic needed
          },
        },
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("banFromOrg.confirmDialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t("banFromOrg.confirmDialog.message")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>
            {t("global.cancel")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmBan} disabled={loading}>
            {loading ? t("global.loading") : t("global.yes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
